import { useState, useCallback, useEffect, useRef } from 'react';
import { Archive, Search, X, RotateCcw, User, Mail, Phone, Loader2 } from 'lucide-react';
import { useQuery, useQueryClient, useMutation, keepPreviousData } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/use-debounce';
import { apiRequest } from '@/lib/queryClient';

/**
 * Shape of an archived customer returned by the API.
 */
interface ArchivedCustomer {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  status: string | null;
  archivedAt: string | null;
  archivedReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface ArchivedResponse {
  data: ArchivedCustomer[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

type ReasonFilter = '' | 'lost' | 'not_qualified' | 'manual' | 'auto_stale';

const REASON_FILTERS: { label: string; value: ReasonFilter }[] = [
  { label: 'All', value: '' },
  { label: 'Lost', value: 'lost' },
  { label: 'Not Qualified', value: 'not_qualified' },
  { label: 'Manual', value: 'manual' },
  { label: 'Stale', value: 'auto_stale' },
];

/**
 * Map archive reason to badge color classes.
 */
function getReasonBadgeClasses(reason: string | null): string {
  switch (reason) {
    case 'won':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
    case 'lost':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
    case 'not_qualified':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800';
    case 'manual':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-400 border-gray-200 dark:border-gray-700';
    case 'auto_stale':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 border-gray-200 dark:border-gray-700';
  }
}

/**
 * Human-readable label for archive reason.
 */
function getReasonLabel(reason: string | null): string {
  switch (reason) {
    case 'won':
      return 'Won';
    case 'lost':
      return 'Lost';
    case 'not_qualified':
      return 'Not Qualified';
    case 'manual':
      return 'Manual';
    case 'auto_stale':
      return 'Auto Stale';
    default:
      return reason || 'Unknown';
  }
}

/**
 * Build the API URL with query parameters for the archived endpoint.
 */
function buildArchiveUrl(page: number, search: string, reason: string): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', '20');
  if (search) params.set('search', search);
  if (reason) params.set('reason', reason);
  return `/api/customers/archived?${params.toString()}`;
}

/**
 * ArchiveSidebar -- A slide-out sheet panel that displays archived, won, and closed
 * customers. Supports search, reason filtering, pagination (load more), and reactivation.
 */
export function ArchiveSidebar() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [reasonFilter, setReasonFilter] = useState<ReasonFilter>('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchInput, 300);

  // Reset page when search or filter changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setPage(1);
  }, []);

  const handleReasonChange = useCallback((value: ReasonFilter) => {
    setReasonFilter(value);
    setPage(1);
  }, []);

  // Accumulate results across pages for "Load more" behavior
  const [accumulatedData, setAccumulatedData] = useState<ArchivedCustomer[]>([]);

  // Track previous search+filter so we can detect when they change
  const prevKeyRef = useRef({ search: debouncedSearch, reason: reasonFilter });

  const queryUrl = buildArchiveUrl(page, debouncedSearch, reasonFilter);

  const { data, isLoading, isFetching } = useQuery<ArchivedResponse>({
    queryKey: ['/api/customers/archived', { page, search: debouncedSearch, reason: reasonFilter }],
    queryFn: async () => {
      const res = await fetch(queryUrl, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    enabled: open,
    placeholderData: keepPreviousData,
  });

  // Sync accumulated data when query data changes
  useEffect(() => {
    if (!data) return;

    const prevKey = prevKeyRef.current;
    const filtersChanged =
      prevKey.search !== debouncedSearch || prevKey.reason !== reasonFilter;

    if (filtersChanged || page === 1) {
      // Filters changed or first page: replace accumulated data
      setAccumulatedData(data.data);
    } else {
      // Subsequent page: append new data (deduplicating by id)
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const newItems = data.data.filter((c) => !existingIds.has(c.id));
        return [...prev, ...newItems];
      });
    }

    prevKeyRef.current = { search: debouncedSearch, reason: reasonFilter };
  }, [data, page, debouncedSearch, reasonFilter]);

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: async (customerId: number) => {
      return apiRequest('POST', `/api/customers/${customerId}/reactivate`);
    },
    onSuccess: (_data, customerId) => {
      toast({
        title: 'Customer Reactivated',
        description: 'The customer has been moved back to the pipeline.',
      });
      // Remove from accumulated list immediately for a snappy UI
      setAccumulatedData((prev) => prev.filter((c) => c.id !== customerId));
      // Invalidate archive, pipeline, and pipeline-stages queries so board refreshes
      queryClient.invalidateQueries({ queryKey: ['/api/customers/archived'] });
      queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline'] });
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Reactivation Failed',
        description: error.message || 'Could not reactivate this customer. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const totalCount = data?.pagination?.totalCount ?? 0;
  const hasNext = data?.pagination?.hasNext ?? false;
  const customers = accumulatedData;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex-shrink-0 gap-1.5 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
          title="View archived leads"
        >
          <Archive className="h-4 w-4" />
          <span className="hidden sm:inline">Archive</span>
        </Button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col"
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-slate-500" />
            Archived Leads
            {totalCount > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {totalCount}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Lost, not qualified, and manually archived customers
          </SheetDescription>
        </SheetHeader>

        {/* Search Bar */}
        <div className="px-6 pt-4 pb-2 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              className="pl-9 pr-9"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-accent"
                onClick={() => handleSearchChange('')}
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Chips */}
        <div className="px-6 pb-3 shrink-0">
          <div className="flex flex-wrap gap-1.5">
            {REASON_FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => handleReasonChange(filter.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  reasonFilter === filter.value
                    ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100'
                    : 'bg-transparent text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Customer List */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 pb-6 space-y-2">
            {/* Loading state for initial load */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mb-3" />
                <p className="text-sm">Loading archived leads...</p>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && customers.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Archive className="h-10 w-10 mb-3 opacity-40" />
                <p className="text-sm font-medium">No archived leads found</p>
                <p className="text-xs mt-1">
                  {debouncedSearch || reasonFilter
                    ? 'Try adjusting your search or filters'
                    : 'Archived leads will appear here'}
                </p>
              </div>
            )}

            {/* Customer cards */}
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {/* Name */}
                    <div className="flex items-center gap-2 mb-1">
                      <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {customer.name}
                      </span>
                    </div>

                    {/* Contact info */}
                    {customer.email && (
                      <div className="flex items-center gap-2 mb-0.5">
                        <Mail className="h-3 w-3 text-slate-400 shrink-0" />
                        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {customer.email}
                        </span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center gap-2 mb-1">
                        <Phone className="h-3 w-3 text-slate-400 shrink-0" />
                        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {customer.phone}
                        </span>
                      </div>
                    )}

                    {/* Reason badge + date */}
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getReasonBadgeClasses(customer.archivedReason)}`}
                      >
                        {getReasonLabel(customer.archivedReason)}
                      </span>
                      {customer.archivedAt && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">
                          {formatDistanceToNow(new Date(customer.archivedAt), {
                            addSuffix: true,
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Restore button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 h-8 w-8 p-0 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                    title="Restore to pipeline"
                    disabled={reactivateMutation.isPending}
                    onClick={() => reactivateMutation.mutate(customer.id)}
                  >
                    {reactivateMutation.isPending &&
                    reactivateMutation.variables === customer.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}

            {/* Load More */}
            {hasNext && customers.length > 0 && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {isFetching ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading...
                    </>
                  ) : (
                    `Load more (${customers.length} of ${totalCount})`
                  )}
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
